/**
 * A faithful port of `java.util.Random`'s linear congruential generator.
 *
 * This exists for one reason: `HoleGeometry.generateTreesInArea` fills a wooded polygon with
 * individual trees using jitter drawn from `java.util.Random`, seeded from the polygon's own
 * coordinates. That determinism is what keeps the trees still — the watch's comment says so
 * outright ("same layout every parse, no flicker"). Swapping in `Math.random()` would make
 * the forest reshuffle on every render, and would also put the web app's rendering of a hole
 * permanently out of step with the watch's.
 *
 * The state is 48 bits, which exceeds what a JS number can hold exactly, so it is kept in a
 * BigInt and masked on every step.
 */

const MULTIPLIER = 0x5deece66dn;
const ADDEND = 0xbn;
const MASK = (1n << 48n) - 1n;

export class JavaRandom {
  private seed: bigint;

  constructor(seed: bigint | number) {
    // Java's constructor scrambles the seed with the multiplier before first use.
    this.seed = (BigInt(seed) ^ MULTIPLIER) & MASK;
  }

  /** The `next(bits)` primitive every other method is built on. */
  private next(bits: number): number {
    this.seed = (this.seed * MULTIPLIER + ADDEND) & MASK;
    const shifted = this.seed >> BigInt(48 - bits);
    // Java returns a signed 32-bit int; only nextDouble's usage matters here, and it treats
    // the value as unsigned, so return the raw magnitude.
    return Number(shifted);
  }

  /** Uniform in [0, 1), bit-for-bit identical to Java's. */
  nextDouble(): number {
    const hi = this.next(26);
    const lo = this.next(27);
    return (hi * 2 ** 27 + lo) / 2 ** 53;
  }
}

/**
 * The seed the watch derives from a polygon, reproduced exactly.
 *
 * `HoleGeometry.kt` computes `(minLng * 1e6 + minLat * 1e6).toLong()` and then constructs
 * `Random(seedSource xor 0x5DEECE66D)`. Java's constructor XORs by the same constant, so the
 * two cancel and the generator's internal state starts at `seedSource` — accidental, but it
 * is the behaviour the watch ships, and reproducing it is the whole point.
 */
export function treeSeedFor(minLongitude: number, minLatitude: number): bigint {
  const source = BigInt(Math.trunc(minLongitude * 1_000_000 + minLatitude * 1_000_000));
  return source ^ MULTIPLIER;
}
